import { CtaGroupWrapper } from '@/app/components/UI/CtaGroup/CtaGroupWrapper'
import { executionVisitCta } from '@/app/models/new-activity-creation-cta'
import { ProjectApiCreateActivityBody } from '@/app/models/projects-service'
import { closeProject } from '@/app/redux/actions/activity-approval'
import {
    editProject,
    NewActivityCreationActions,
    releaseProject,
} from '@/app/redux/actions/activity-creation'
import { cancelProject, recallProject } from '@/app/redux/actions/activity-execution'
import { getSaveAsDraftBody } from '@/app/utils/activityAdapter'
import { SmartlyActionItems, User } from '@Techedge/digitalportal-shared'
import { useState } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import DialogCancelVisit from '../VisitExecutionAction/DialogCancelVisit/DialogCancelVisit'
import DialogCloseVisit from '../VisitExecutionAction/DialogCloseVisit/DialogCloseVisit'

export interface VisitFooterProps {
    projectDetails: ProjectApiCreateActivityBody
    user: User
    setShowDuplicateDialog: (value: boolean) => void
    setShowEditDialogConfirm: (value: boolean) => void
    setShowReassignDialog: (value: boolean) => void
    editProject: (newProject, successMessage: string, statusAction: string) => void
    closeProject: (
        projectId: string,
        storeIds: string[],
        successMessage: string,
        storeIdList
    ) => void
    releaseProject: (projectId: string, successMessage: string) => void
    recallProject: (projectId: string, successMessage: string) => void
    cancelProject: (
        projectId: string,
        successMessage: string,
        body?: { macroreason: string; reason: string }
    ) => void
}

const VisitFooter = ({
    projectDetails,
    user,
    setShowDuplicateDialog,
    setShowEditDialogConfirm,
    setShowReassignDialog,
    editProject,
    closeProject,
    releaseProject,
    recallProject,
    cancelProject,
}: VisitFooterProps) => {
    const componentClass = 'smart-modals-activity-execution'

    const [showDialogCancelVisit, setShowDialogCancelVisit] = useState(false)
    const [showDialogCloseVisit, setShowDialogCloseVisit] = useState(false)

    const intl = useIntl()

    const ctaHandler = (action: string) => {
        switch (action) {
            case NewActivityCreationActions.SAVE_AS_DRAFT:
                let newProjectDetails = { ...projectDetails }
                editProject(
                    getSaveAsDraftBody(newProjectDetails),
                    intl.formatMessage({
                        id: 'modals.activity.create.draft.success.message',
                        defaultMessage: 'Project saved as draft successfully!',
                    }),
                    'draft'
                )
                break
            case NewActivityCreationActions.CLONE:
                setShowDuplicateDialog(true)
                break
            case NewActivityCreationActions.CLOSE_PROJECT:
                setShowDialogCloseVisit(true)
                break
            case NewActivityCreationActions.RECALL_PROJECT:
                recallProject(
                    projectDetails._id,
                    intl.formatMessage({
                        id: 'modals.activity.approval.recall.success.message',
                        defaultMessage: 'Project recalled successfully!',
                    })
                )
                break
            case NewActivityCreationActions.EDIT:
                setShowEditDialogConfirm(true)
                break
            case NewActivityCreationActions.CANCEL:
                setShowDialogCancelVisit(true)
                break
            case NewActivityCreationActions.RELEASE:
                releaseProject(
                    projectDetails._id,
                    intl.formatMessage({
                        id: 'modals.activity.create.release.success.message',
                        defaultMessage: 'Project released successfully!',
                    })
                )
                break
            case NewActivityCreationActions.REASSIGN:
                setShowReassignDialog(true)
                break
        }
    }

    const onConfirmCancel = (macroreason: string, reason: string) => {
        setShowDialogCancelVisit(false)
        cancelProject(
            projectDetails._id,
            intl.formatMessage({
                id: 'modals.activity.approval.cancel.success.message',
                defaultMessage: 'Store redo successfully!',
            }),
            { macroreason, reason }
        )
    }

    const onConfirmClose = () => {
        setShowDialogCloseVisit(false)
        closeProject(
            projectDetails._id,
            projectDetails?.storesIdList,
            intl.formatMessage({
                id: 'modals.activity.approval.close.project.success.message',
                defaultMessage: 'Project closed successfully!',
            }),
            projectDetails?.storesIdList
        )
    }

    let ctaExecApproval = executionVisitCta(
        projectDetails?.projectType as SmartlyActionItems,
        projectDetails,
        user
    )

    return (
        !!projectDetails?._id && (
            <>
                <CtaGroupWrapper
                    ctaConfig={ctaExecApproval}
                    componentClass={componentClass}
                    ctaHandler={ctaHandler}
                />
                {showDialogCancelVisit && (
                    <DialogCancelVisit
                        dialogVisible={showDialogCancelVisit}
                        onHide={() => setShowDialogCancelVisit(false)}
                        onConfirm={onConfirmCancel}
                    />
                )}
                {showDialogCloseVisit && (
                    <DialogCloseVisit
                        dialogVisible={showDialogCloseVisit}
                        onHide={() => setShowDialogCloseVisit(false)}
                        onConfirm={onConfirmClose}
                    />
                )}
            </>
        )
    )
}

const mapDispatchToProps = (dispatch: any) => ({
    editProject: (newProject, successMessage: string, statusAction: string) =>
        dispatch(editProject(newProject, null, null, null, successMessage, statusAction)),
    closeProject: (projectId: string, storeIds: string[], successMessage: string, storeIdList) => {
        dispatch(closeProject(projectId, storeIds, successMessage, storeIdList))
    },
    recallProject: (projectId: string, successMessage: string) =>
        dispatch(recallProject(projectId, successMessage)),
    releaseProject: (projectId: string, successMessage: string) =>
        dispatch(releaseProject(projectId, successMessage)),
    cancelProject: (
        projectId: string,
        successMessage: string,
        body: { macroreason: string; reason: string }
    ) => dispatch(cancelProject(projectId, successMessage, body)),
})

export default connect(() => ({}), mapDispatchToProps)(VisitFooter)
